import { isPlainObject } from "lodash-es";
import * as DOMPurify from "dompurify";
import moment from "moment";
import $ from "jquery";
import ReactDOM from "react-dom";
import ReactDOMServer from "react-dom/server";
import classnames from "classnames";

import { destroyPopovers } from "common/utils";
import { store } from "common/store";

import { pazienHistory } from "common/components/pazienHistory";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt } from "@fortawesome/pro-light-svg-icons";
import { faTimesCircle } from "@fortawesome/pro-solid-svg-icons";

import { MonthlyCalendar } from "./monthlyCalendar";
import { QuarterCalendar } from "./quarterCalendar";

import daterangepickerTemplate from "./daterangepicker.jst";

export function DateRangePicker(element, options = {}, cb = () => {}) {
    this.locale = {
        format: moment.localeData().longDateFormat("L"),
        separator: " - ",
        applyLabel: "Apply",
        cancelLabel: "Cancel",
        weekLabel: "W",
        daysOfWeek: moment.weekdaysMin(),
        monthNames: moment.monthsShort(),
        firstDay: moment.localeData().firstDayOfWeek(),
        ...options.locale
    };

    // update day names order to firstDay
    for (let i = this.locale.firstDay; i > 0; i--) {
        this.locale.daysOfWeek.push(this.locale.daysOfWeek.shift());
    }
    // default settings for options
    Object.assign(this, {
        parentEl: $("body"),
        element: $(element),
        options,
        calendars: options.calendars || ["day"],
        activeCalendar: options.activeCalendar || "day",
        
        startDate: options.startDate
            ? moment(options.startDate, this.locale.format)
            : moment().startOf("day"),
        endDate: options.endDate
            ? moment(options.endDate, this.locale.format)
            : moment().endOf("day"),
        datetype: {
            datetypes: options.datetypes || []
        },
        minDate: options.minDate
            ? moment(options.minDate, this.locale.format)
            : false,
        maxDate: options.maxDate
            ? moment(options.maxDate, this.locale.format)
            : false,
        dateLimit: isPlainObject(options.dateLimit)
            ? options.dateLimit
            : false,
        autoUpdateInput: typeof options.autoUpdateInput === "boolean"
            ? options.autoUpdateInput
            : true,
        showDropdowns: options.showDropdowns || false,
        showWeekNumbers: options.showWeekNumbers || false,
        showISOWeekNumbers: options.showISOWeekNumbers || false,
        linkedCalendars: options.linkedCalendars || false,
        has_datetype: options.has_datetype || false,
        has_data_availability: options.has_data_availability || false,
        has_data_availability_legend: options.has_data_availability_legend || false,
        has_date_comparison: options.has_date_comparison || false,
        
        opens: options.opens || "right",
        drops: options.drops || "down",
        applyClass: options.applyClass || "btn-primary",
        cancelClass: options.cancelClass || "btn-secondary dismiss",
        buttonClasses: options.buttonClasses || "btn",
        
        isInvalidDate: options.isInvalidDate || function() { return false; },
        isCustomDate: options.isCustomDate || function() { return false; },
        isPermittedDate: options.isPermittedDate || function() { return true; },
        dateTooltip: options.dateTooltip || function() { return ""; },
        beforeApply: options.beforeApply || function() { return Promise.resolve(true); },
        onLockedClick: options.onLockedClick || function() {}
    });
    
    this.state = {
        activeCompareToTab: options.activeCompareToTab || 0
    };
    
    this.callback = cb;
    
    // some state information
    this.isShowing = false;
    this.leftCalendar = {};
    this.rightCalendar = {};
    
    // custom options from user
    this.ranges = {};
    
    // sanity check for bad options
    if (this.minDate && this.startDate.isBefore(this.minDate)) { this.startDate = this.minDate.clone(); }
    
    // sanity check for bad options
    if (this.maxDate && this.endDate.isAfter(this.maxDate)) { this.endDate = this.maxDate.clone(); }
    
    if (this.element.hasClass("pull-right")) { this.opens = "left"; }
    
    if (this.element.hasClass("dropup")) { this.drops = "up"; }
    
    this._hasVirtualEndDate = false;
    
    if (this.has_date_comparison) {
        this.state.compare_to = [{
            startDate: this.startDate,
            endDate: this.endDate
        }, ...this.options.compare_to || []];
    }
    
    this.renderContainer();
    
    this.bindElementEvents();
    
    // If attached to a text input, set the initial value
    if (this.element.is("input") && this.autoUpdateInput) {
        this.element.val(this.startDate.format(this.locale.format) + this.locale.separator + this.endDate.format(this.locale.format));
        this.element.trigger("change");
    }
}

DateRangePicker.prototype = {
    constructor: DateRangePicker,
    
    bindElementEvents() {
        if (this.element.is("input") || this.element.is("button")) {
            this.element.on({
                "click.daterangepicker": this.show.bind(this),
                "focus.daterangepicker": this.show.bind(this),
                "keyup.daterangepicker": this.elementChanged.bind(this),
                "keydown.daterangepicker": this.keydown.bind(this)
            });
        } else {
            this.element.on("click.daterangepicker", this.toggle.bind(this));
        }
    },
    
    setStartDate(startDate) {
        if (typeof startDate === "string") { this.startDate = moment(startDate, this.locale.format); }
        
        if (typeof startDate === "object") { this.startDate = moment(startDate); }
        
        this.startDate = this.startDate.startOf(this.activeCalendar);
        
        if (this.minDate && this.startDate.isBefore(this.minDate)) {
            this.startDate = this.minDate.clone();
        }
        
        if (this.maxDate && this.startDate.isAfter(this.maxDate)) {
            this.startDate = this.maxDate.clone();
        }
        
        if (!this.isShowing) { this.updateElement(); }
    
        this.updateMonthsInView();
    },
    
    setEndDate(endDate) {
        if (moment.isMoment(endDate)) {
            this.endDate = endDate.clone();
        } else if (typeof endDate === "string") {
            this.endDate = moment(endDate, this.locale.format);
        } else if (typeof endDate === "object") {
            this.endDate = moment(endDate);
        }
        
        this.endDate = this.endDate.endOf("day");
        
        if (this.endDate.isBefore(this.startDate)) { this.endDate = this.startDate.clone(); }
        
        if (this.maxDate && this.endDate.isAfter(this.maxDate)) { this.endDate = this.maxDate.clone(); }
        
        if (this.dateLimit && this.startDate.clone().add(this.dateLimit).isBefore(this.endDate)) { this.endDate = this.startDate.clone().add(this.dateLimit); }
        
        if (this.activeCalendar === "month" || this.activeCalendar === "quarter") {
            this.endDate = this.endDate.endOf(this.activeCalendar);
        }
        
        if (!this.isShowing) { this.updateElement(); }
        
        this.updateMonthsInView();
    },
    
    setDatesFromActiveTab() {
        if (this.has_date_comparison) {
            // Re-render the tabs with the new start and end dates
            if (!this.state.compare_to[this.state.activeCompareToTab]) {
                this.state.activeCompareToTab = this.state.activeCompareToTab - 1;
            }
            this.startDate = this.state.compare_to[this.state.activeCompareToTab].startDate;
            this.endDate = this.state.compare_to[this.state.activeCompareToTab].endDate;
        }
    },
    
    updateView() {
        if (this.endDate) {
            this.container.find("input[name=\"daterangepicker_end\"]").removeClass("active");
            this.container.find("input[name=\"daterangepicker_start\"]").addClass("active");
        } else {
            this.container.find("input[name=\"daterangepicker_end\"]").addClass("active");
            this.container.find("input[name=\"daterangepicker_start\"]").removeClass("active");
        }
        switch (this.activeCalendar) {
        case "day": {
            this.updateMonthsInView();
            this.updateCalendars();
        
            break;
        }
        case "month": {
            this.updateMonthlyCalendar();
        
            break;
        }
        case "quarter": {
            this.updateQuarterlyCalendar();
        
            break;
        }
        default: { break; }
        }
        
        this.updateFormInputs();
        this.handleCompareToChange();
    },
    
    updateMonthsInView() {
        if (this.activeCalendar !== "day") return;
        
        const startDateMonthISO = this.startDate?.format("YYYY-MM");
        const endDateMonthISO = this.endDate?.format("YYYY-MM");
        const leftCalendarMonthISO = this.leftCalendar.month?.format("YYYY-MM");
        const rightCalendarMonthISO = this.rightCalendar.month?.format("YYYY-MM");
        
        if (this.endDate) {
            // if both dates are visible already, do nothing
            if (this.leftCalendar.month && this.rightCalendar.month
                && (startDateMonthISO === leftCalendarMonthISO || startDateMonthISO === rightCalendarMonthISO)
                && (endDateMonthISO === leftCalendarMonthISO || endDateMonthISO === rightCalendarMonthISO)
            ) {
                return;
            }
            
            this.leftCalendar.month = this.startDate ? this.startDate.clone().date(2) : this.endDate.clone().date(2).subtract(1, "month");
            if (!this.linkedCalendars && (this.endDate.month() !== this.startDate.month() || this.endDate.year() !== this.startDate.year())) {
                this.rightCalendar.month = this.endDate.clone().date(2);
            } else {
                this.rightCalendar.month = this.startDate.clone().date(2).add(1, "month");
            }
        } else if (!startDateMonthISO && !endDateMonthISO) {
            // If both dates are null then select current 30 days period
            this.leftCalendar.month = moment().date(2).subtract(1, "month");
            this.rightCalendar.month = moment().date(2);
        } else if (leftCalendarMonthISO !== startDateMonthISO && rightCalendarMonthISO !== startDateMonthISO) {
            this.leftCalendar.month = this.startDate.clone().date(2);
            this.rightCalendar.month = this.startDate.clone().date(2).add(1, "month");
        }
        
        if (this.maxDate && this.linkedCalendars && this.rightCalendar.month > this.maxDate) {
            this.rightCalendar.month = this.maxDate.clone().date(2);
            this.leftCalendar.month = this.maxDate.clone().date(2).subtract(1, "month");
        }
    },
    
    updateCalendars() {
        this.renderCalendar("left");
        this.renderCalendar("right");
        
        // highlight any predefined range matching the current start and end dates
        this.container.find(".ranges li").removeClass("active");
        if (this.endDate === null || typeof this.endDate === "undefined") return;
        
        this.calculateChosenLabel();
    },
    
    updateMonthlyCalendar() {
        const userPermissionDate = moment(store.getState().permissions.user_permissions.earliest_start_date).startOf("day");
        const merchantCreateDate = moment(store.getState().common.merchant_config.created);
        // super user min date is 1 AD so need to bound it by merchant create date.
        const permissionsMinDate = merchantCreateDate > userPermissionDate ? merchantCreateDate : userPermissionDate;
        const twentyfive = moment.utc().startOf("month").subtract(24, "months");
        const greaterMinDate = twentyfive < permissionsMinDate ? twentyfive : permissionsMinDate;
        const minimumRenderDate = this.minDate && this.minDate > twentyfive ? this.minDate : greaterMinDate;
        ReactDOM.render(<MonthlyCalendar
            startDate={this.startDate && this.startDate.clone()}
            endDate={this.endDate && this.endDate.clone()}
            maxDate={this.maxDate.clone()}
            selectDate={(date) => this.processDateClick(date)}
            isPermittedDate={this.isPermittedDate}
            startYear={minimumRenderDate}
            dateTooltip={this.dateTooltip}
        />, this.container.find(".month-picker").get(0));
    },
    
    updateQuarterlyCalendar() {
        ReactDOM.render(<QuarterCalendar
            startDate={this.startDate && this.startDate.clone()}
            endDate={this.endDate && this.endDate.clone()}
            maxDate={this.maxDate.clone()}
            selectDate={(date) => this.processDateClick(date)}
            isPermittedDate={this.isPermittedDate}
        />, this.container.find(".quarter-picker").get(0));
    },
    
    renderCalendar(side) {
        //
        // Build the matrix of dates that will populate the calendar
        //
        
        let calendar = side === "left" ? this.leftCalendar : this.rightCalendar;
        const month = calendar.month.month();
        const year = calendar.month.year();
        const hour = calendar.month.hour();
        const minute = calendar.month.minute();
        const second = calendar.month.second();
        const daysInMonth = moment([year, month]).daysInMonth();
        const firstDay = moment([year, month, 1]);
        const lastDay = moment([year, month, daysInMonth]);
        const lastMonth = moment(firstDay).subtract(1, "month").month();
        const lastYear = moment(firstDay).subtract(1, "month").year();
        const daysInLastMonth = moment([lastYear, lastMonth]).daysInMonth();
        const dayOfWeek = firstDay.day();
        const userPermissionDate = moment(store.getState().permissions.user_permissions.earliest_start_date).startOf("day");
        const merchantCreateDate = moment(store.getState().common.merchant_config.created);
        const permissionsMinDate = merchantCreateDate > userPermissionDate ? merchantCreateDate : userPermissionDate;
        // initialize a 6 rows x 7 columns array for the calendar
        calendar = [];
        calendar.firstDay = firstDay;
        calendar.lastDay = lastDay;
        
        for (let i = 0; i < 6; i++) {
            calendar[i] = [];
        }
        
        // populate the calendar with date objects
        let startDay = daysInLastMonth - dayOfWeek + this.locale.firstDay + 1;
        if (startDay > daysInLastMonth) { startDay -= 7; }
        
        if (dayOfWeek === this.locale.firstDay) { startDay = daysInLastMonth - 6; }
        
        let curDate = moment([lastYear, lastMonth, startDay, 12, minute, second]);
        
        for (let i = 0, col = 0, row = 0; i < 42; i++, col++, curDate = moment(curDate).add(24, "hour")) {
            if (i > 0 && col % 7 === 0) {
                col = 0;
                row++;
            }
            calendar[row][col] = curDate.clone().hour(hour).minute(minute).second(second);
            curDate.hour(12);
            
            if (this.minDate && calendar[row][col].format("YYYY-MM-DD") === this.minDate.format("YYYY-MM-DD") && calendar[row][col].isBefore(this.minDate) && side === "left") {
                calendar[row][col] = this.minDate.clone();
            }
            
            if (this.maxDate && calendar[row][col].format("YYYY-MM-DD") === this.maxDate.format("YYYY-MM-DD") && calendar[row][col].isAfter(this.maxDate) && side === "right") {
                calendar[row][col] = this.maxDate.clone();
            }
        }
        
        // make the calendar object available to hoverDate/clickDate
        if (side === "left") {
            this.leftCalendar.calendar = calendar;
        } else {
            this.rightCalendar.calendar = calendar;
        }
        
        //
        // Display the calendar.
        //
        const twentyfive = moment.utc().startOf("month").subtract(24, "months");
        const greaterMinDate = twentyfive < permissionsMinDate ? twentyfive : permissionsMinDate;
        const minimumRenderDate = this.minDate && this.minDate > twentyfive ? this.minDate : greaterMinDate;
        const minDate = side === "left" ? minimumRenderDate : this.startDate;
        // const minDate = (side === "left") ? minimumAllowedDate : this.startDate;
        let maxDate = this.maxDate;
        
        let html = `<table class="table-condensed">
                    <thead>
                        <tr>`;
        
        // add empty cell for week number
        if (this.showWeekNumbers || this.showISOWeekNumbers) { html += "<th></th>"; }
        
        if ((!minDate || minDate.isBefore(calendar.firstDay)) && (!this.linkedCalendars || side === "left")) {
            html += "<th class=\"prev available\"><i class=\"fal fa-chevron-left\"></i></th>";
        } else {
            html += "<th></th>";
        }
        
        let dateHtml = this.locale.monthNames[calendar[1][1].month()] + calendar[1][1].format(" YYYY");
        
        if (this.showDropdowns) {
            const currentMonth = calendar[1][1].month();
            const currentYear = calendar[1][1].year();
            const maxYear = (maxDate && maxDate.year()) || (currentYear + 5);
            const minYear = (minDate && minDate.year()) || moment(store.getState().common.merchant_config.created).year();
            const inMinYear = currentYear === minYear;
            const inMaxYear = currentYear === maxYear;
            
            let monthHtml = "<select class=\"monthselect\">";
            for (let m = 0; m < 12; m++) {
                let disableMonth = false;
                if (inMinYear && (side === "left") && (minDate && (m < minDate.month()))) {
                    disableMonth = true;
                }
                
                if (maxDate && inMaxYear && m > maxDate.month()) {
                    disableMonth = true;
                }
    
                monthHtml += `<option value='${m}'
                                ${m === currentMonth ? " selected='selected'" : ""}
                                ${disableMonth ? " disabled='disabled'" : ""}
                                >${this.locale.monthNames[m]}</option>`;
            }
            monthHtml += "</select>";
            
            let yearHtml = "<select class=\"yearselect\">";
            for (let y = minYear; y <= maxYear; y++) {
                yearHtml += `<option value="${y}"${
                    y === currentYear ? " selected=\"selected\"" : ""
                }>${y}</option>`;
            }
            yearHtml += "</select>";
            
            dateHtml = monthHtml + yearHtml;
        }
        
        html += `<th colspan="5" class="month">${dateHtml}</th>`;
        if ((!maxDate || maxDate.isAfter(calendar.lastDay)) && (!this.linkedCalendars || side === "right")) {
            html += "<th class=\"next available\"><i class=\"fal fa-chevron-right\"></i></th>";
        } else {
            html += "<th></th>";
        }
        
        html += "</tr>";
        html += "<tr>";
        
        // add week number label
        if (this.showWeekNumbers || this.showISOWeekNumbers) { html += `<th class="week">${this.locale.weekLabel}</th>`; }

        $.each(this.locale.daysOfWeek, (index, _dayOfWeek) => {
            html += `<th>${_dayOfWeek}</th>`;
        });
        
        html += "</tr></thead><tbody>";
        
        // adjust maxDate to reflect the dateLimit setting in order to
        // grey out end dates beyond the dateLimit
        // eslint-disable-next-line eqeqeq
        if (this.endDate == null && this.dateLimit) {
            const maxLimit = this.startDate.clone().add(this.dateLimit).endOf("day");
            if (!maxDate || maxLimit.isBefore(maxDate)) {
                maxDate = maxLimit;
            }
        }
        
        for (let row = 0; row < 6; row++) {
            html += "<tr>";
            
            // add week number
            if (this.showWeekNumbers) { html += `<td class="week">${calendar[row][0].week()}</td>`; } else if (this.showISOWeekNumbers) { html += `<td class="week">${calendar[row][0].isoWeek()}</td>`; }
            
            for (let col = 0; col < 7; col++) {
                const classes = [];
                
                // highlight today's date
                if (calendar[row][col].isSame(new Date(), "day")) { classes.push("today"); }
                
                // highlight weekends
                if (calendar[row][col].isoWeekday() > 5) { classes.push("weekend"); }
                
                // grey out the dates in other months displayed at beginning and end of this calendar
                if (calendar[row][col].month() !== calendar[1][1].month()) { classes.push("off"); }
                
                // don't allow selection of dates before the minimum date
                if (this.minDate && calendar[row][col].isBefore(this.minDate, "day")) { classes.push("disabled"); }
                
                // don't allow selection of dates after the maximum date
                if (maxDate && calendar[row][col].isAfter(maxDate, "day")) { classes.push("disabled"); }
                
                // don't allow selection of date if a custom function decides it's invalid
                if (this.isInvalidDate(calendar[row][col])) { classes.push("disabled"); }
                
                // highlight the currently selected start date
                if (this.startDate !== null && calendar[row][col].format("YYYY-MM-DD") === this.startDate.format("YYYY-MM-DD")) { classes.push("active", "start-date"); }
                
                // highlight the currently selected end date
                // eslint-disable-next-line eqeqeq
                if (this.endDate != null && calendar[row][col].format("YYYY-MM-DD") === this.endDate.format("YYYY-MM-DD")) { classes.push("active", "end-date"); }
                
                // highlight dates in-between the selected dates
                // eslint-disable-next-line eqeqeq
                if (this.endDate != null && calendar[row][col] > this.startDate && calendar[row][col] < this.endDate) { classes.push("in-range"); }
                
                // apply custom classes for this date
                const isCustom = this.isCustomDate(calendar[row][col]);
                if (isCustom !== false) {
                    if (typeof isCustom === "string") { classes.push(isCustom); } else { Array.prototype.push.apply(classes, isCustom); }
                }
                
                // Define data-attribute for the cell
                const attrs = {
                    "data-title": `r${row}c${col}`
                };
                
                // Check if date is permitted and if not then apply a lock
                const isPermitted = this.isPermittedDate(calendar[row][col]);
                if (!isPermitted) {
                    if (calendar[row][col].isBefore(moment.utc().startOf("month").subtract(25, "months"))) {
                        classes.push("past");
                    }
                    classes.push("locked", "disabled");
                }
                
                let cname = "";
                let disabled = false;
                for (let i = 0; i < classes.length; i++) {
                    cname += `${classes[i]} `;
                    if (classes[i] === "disabled") { disabled = true; }
                }
                if (!disabled) { cname += "available"; }
                
                const str_attrs = Object.entries(attrs).map(([name, value]) => `${name}="${value}"`).join(" ");
                let cell_content = calendar[row][col].date();
                if (!isPermitted) {
                    const dateTooltip = this.dateTooltip(calendar[row][col]);
                    if (calendar[row][col].isBefore(moment.utc().startOf("month").subtract(25, "months"))) {
                        cell_content = `<span class='tooltipped tooltipped-n' aria-label='${dateTooltip}'>${calendar[row][col].date()}</span>`;
                    } else {
                        cell_content = `<span class='tooltipped tooltipped-n' aria-label='${dateTooltip}'><i class='fas fa-lock'></i></span>`;
                    }
                }
                html += `<td class="${cname.replace(/^\s+|\s+$/g, "")}" ${str_attrs}>${cell_content}</td>`;
            }
            html += "</tr>";
        }
        
        html += "</tbody>";
        html += "</table>";
        
        const $tableContainer = this.container.find(`.calendar.${side} .calendar-table`);
        if ($tableContainer.html() !== html) {
            $tableContainer.html(DOMPurify.sanitize(html));
        }
    },
    
    updateFormInputs() {
        // ignore mouse movements while an above-calendar text input has focus
        if (this.container.find("input[name=daterangepicker_start]").is(":focus")
            || this.container.find("input[name=daterangepicker_end]").is(":focus")) return;
        
        let startValue = "Select start date";
        if (this.startDate !== null) {
            startValue = this.startDate.format(this.locale.format);
        }
        this.container.find("input[name=daterangepicker_start]").val(startValue);
    
        let endValue = "Select end date";
        if (this.endDate !== null) {
            endValue = this.endDate.format(this.locale.format);
        }
        this.container.find("input[name=daterangepicker_end]").val(endValue);
        
        if (this.startDate && this.endDate && (this.startDate.isBefore(this.endDate) || this.startDate.isSame(this.endDate))) {
            this.container.find("button.applyBtn").removeAttr("disabled");
        } else {
            this.container.find("button.applyBtn").attr("disabled", "disabled");
        }
    },
    
    move() {
        let parentOffset = { top: 0, left: 0 };
        let containerTop;
        let parentRightEdge = $(window).width();
        if (!this.parentEl.is("body")) {
            parentOffset = {
                top: this.parentEl.offset().top - this.parentEl.scrollTop(),
                left: this.parentEl.offset().left - this.parentEl.scrollLeft()
            };
            parentRightEdge = this.parentEl[0].clientWidth + this.parentEl.offset().left;
        }
        
        if (this.drops === "up") { containerTop = this.element.offset().top - this.container.outerHeight() - parentOffset.top; } else { containerTop = this.element.offset().top + this.element.outerHeight() - parentOffset.top; }
        this.container[this.drops === "up" ? "addClass" : "removeClass"]("dropup");
        
        if (this.opens === "left") {
            this.container.css({
                top: containerTop,
                right: parentRightEdge - this.element.offset().left - this.element.outerWidth(),
                left: "auto"
            });
            if (this.container.offset().left < 0) {
                this.container.css({
                    right: "auto",
                    left: 9
                });
            }
        } else if (this.opens === "center") {
            this.container.css({
                top: containerTop,
                left: this.element.offset().left - parentOffset.left + this.element.outerWidth() / 2
                    - this.container.outerWidth() / 2,
                right: "auto"
            });
            if (this.container.offset().left < 0) {
                this.container.css({
                    right: "auto",
                    left: 9
                });
            }
        } else {
            this.container.css({
                top: containerTop,
                left: this.element.offset().left - parentOffset.left,
                right: "auto"
            });
            if (this.container.offset().left + this.container.outerWidth() > $(window).width()) {
                this.container.css({
                    left: "auto",
                    right: 0
                });
            }
        }
    },
    
    show(event) {
        if (this.isShowing) return;
        
        // Create a click proxy that is private to this instance of datepicker, for unbinding
        this._outsideClickProxy = (e) => { this.outsideClick(e); };
        
        // Bind global datepicker mousedown for hiding and
        $(document)
            .on("mousedown.daterangepicker", this._outsideClickProxy)
            // also support mobile devices
            .on("touchend.daterangepicker", this._outsideClickProxy)
            // also explicitly play nice with Bootstrap dropdowns, which stopPropagation when clicking them
            .on("click.daterangepicker", "[data-toggle=dropdown]", this._outsideClickProxy)
            // and also close when focus changes to outside the picker (eg. tabbing between controls)
            .on("focusin.daterangepicker", this._outsideClickProxy);
        
        // Reposition the picker if the window is resized while it's open
        $(window).on("resize.daterangepicker", (e) => { this.move(e); });
        
        this.oldStartDate = this.startDate.clone();
        this.oldEndDate = this.endDate.clone();
        
        if (event && this.has_date_comparison) {
            // Open active tab based on selector index
            const selector = event.target.classList.contains("calendar-selector") ? event.target : event.target.closest(".calendar-selector");
            this.state.activeCompareToTab = Array.from(selector.parentNode.children).indexOf(selector);
            this.setDatesFromActiveTab();
        }
        
        this.updateView();
        this.handleDatetypeChange();
        this.handleCompareToChange();
        
        this.container.show();
        this.move();
        this.element.trigger("show.daterangepicker", this);
        this.isShowing = true;
    },
    
    hide(e) {
        if (!this.isShowing) return;
        
        // incomplete date selection, revert to last values
        if (!this.endDate) {
            this.startDate = this.oldStartDate.clone();
            this.endDate = this.oldEndDate.clone();
        }
        
        // if a new date range was selected, invoke the user callback function
        if (!this.startDate.isSame(this.oldStartDate) || !this.endDate.isSame(this.oldEndDate)) { this.callback(this.startDate, this.endDate, this.chosenLabel); }
        
        // if picker is attached to a text input, update it
        this.updateElement();
        
        $(document).off(".daterangepicker");
        $(window).off(".daterangepicker");
        this.container.hide();
        this.element.trigger("hide.daterangepicker", this);
        this.isShowing = false;
    },
    
    toggle(e) {
        if (this.isShowing) {
            this.hide(e);
        } else {
            this.show(e);
        }
    },
    
    outsideClick(e) {
        const target = $(e.target);
        // if the page is clicked anywhere except within the daterangerpicker/button
        // itself then call this.hide()
        if (e.type === "focusin" // ie modal dialog fix
            || target.closest(this.element).length
            || target.closest(this.container).length
            || target.closest(".calendar-table").length
            || target.closest(".swal-overlay").length) return;
        
        this.hide();
        this.element.trigger("outsideClick.daterangepicker", this);
    },
    
    showCalendars() {
        this.container.addClass("show-calendar");
        this.move();
        this.element.trigger("showCalendar.daterangepicker", this);
    },
    
    hideCalendars() {
        this.container.removeClass("show-calendar");
        this.element.trigger("hideCalendar.daterangepicker", this);
    },
    
    renderRanges(ranges = this.options.ranges) {
        this.ranges = {};
        // eslint-disable-next-line guard-for-in,no-restricted-syntax
        for (const range in ranges) {
            let [start, end] = ranges[range];
            
            // If the start or end date exceed those allowed by the minDate or dateLimit
            // options, shorten the range to the allowable period.
            if (this.minDate && start.isBefore(this.minDate)) { start = this.minDate.clone(); }
            
            let maxDate = this.maxDate;
            if (this.dateLimit && maxDate && start.clone().add(this.dateLimit).isAfter(maxDate)) { maxDate = start.clone().add(this.dateLimit); }
            if (maxDate && end.isAfter(maxDate)) { end = maxDate.clone(); }
            
            // If the end of the range is before the minimum or the start of the range is
            // after the maximum, don't display this range option at all.
            if (!((this.minDate && end.isBefore(this.minDate, "day")) || (maxDate && start.isAfter(maxDate, "day")))) {
                // Support unicode chars in the range names.
                const elem = document.createElement("textarea");
                elem.innerHTML = range;
                const rangeHtml = elem.value;
                
                this.ranges[rangeHtml] = [start, end];
            }
        }
    
        const list = Object.keys(this.ranges).map((range) => `<li data-range-key="${range}">${range}</li>`).join("");
        this.container.find(".ranges .range-list").empty().append(list);
        this.calculateChosenLabel();
    },
    
    clickRange(e) {
        const label = e.target.dataset.rangeKey;
        this.chosenLabel = label;
        
        const [start, end] = this.ranges[label];
        this.startDate = start;
        this.endDate = end;
        
        this.startDate.startOf("day");
        this.endDate.endOf("day");
        
        this.handleCompareToChange();
    
        this.updateView();
    },
    
    clickPrev(e) {
        const cal = $(e.target).parents(".calendar");
        if (cal.hasClass("left")) {
            this.leftCalendar.month.subtract(1, "month");
            if (this.linkedCalendars) { this.rightCalendar.month.subtract(1, "month"); }
        } else {
            this.rightCalendar.month.subtract(1, "month");
        }
        this.updateCalendars();
    },
    
    clickNext(e) {
        const cal = $(e.target).parents(".calendar");
        if (cal.hasClass("left")) {
            this.leftCalendar.month.add(1, "month");
        } else {
            this.rightCalendar.month.add(1, "month");
            if (this.linkedCalendars) { this.leftCalendar.month.add(1, "month"); }
        }
        this.updateCalendars();
    },
    
    hoverDate(e) {
        // ignore dates that can't be selected
        if (!$(e.target).hasClass("available")) return;
        
        // have the text inputs above calendars reflect the date being hovered over
        let title = $(e.target).attr("data-title");
        let row = title.substr(1, 1);
        let col = title.substr(3, 1);
        let cal = $(e.target).parents(".calendar");
        const date = cal.hasClass("left") ? this.leftCalendar.calendar[row][col] : this.rightCalendar.calendar[row][col];
        
        if (this.endDate && !this.container.find("input[name=daterangepicker_start]").is(":focus")) {
            this.container.find("input[name=daterangepicker_start]").val(date.format(this.locale.format));
        } else if (!this.endDate && !this.container.find("input[name=daterangepicker_end]").is(":focus")) {
            this.container.find("input[name=daterangepicker_end]").val(date.format(this.locale.format));
        }
        
        // highlight the dates between the start date and the date being hovered as a potential end date
        const leftCalendar = this.leftCalendar;
        const rightCalendar = this.rightCalendar;
        const startDate = this.startDate;
        if (!this.endDate) {
            this.container.find(".calendar tbody td").each((index, el) => {
                // skip week numbers, only look at dates
                if ($(el).hasClass("week")) return;
                
                title = $(el).attr("data-title");
                row = title.substr(1, 1);
                col = title.substr(3, 1);
                cal = $(el).parents(".calendar");
                const dt = cal.hasClass("left") ? leftCalendar.calendar[row][col] : rightCalendar.calendar[row][col];
                
                if ((dt.isAfter(startDate) && dt.isBefore(date)) || dt.isSame(date, "day")) {
                    $(el).addClass("in-range");
                } else {
                    $(el).removeClass("in-range");
                }
            });
        }
    },
    
    clickLockedDate(e) {
        this.onLockedClick();
    },
    
    clickDate(e) {
        const $target = $(e.target);
        
        if (!$target.hasClass("available")) return;
        
        const title = $target.attr("data-title");
        const row = title.substr(1, 1);
        const col = title.substr(3, 1);
        
        const date = $target.parents(".calendar").hasClass("left")
            ? this.leftCalendar.calendar[row][col]
            : this.rightCalendar.calendar[row][col];
        
        this.processDateClick(date);
        
        // This is to cancel the blur event handler if the mouse was in one of the inputs
        e.stopPropagation();
    },
    
    // * alternate between selecting a start and end date for the range,
    processDateClick(date) {
        if (this._hasVirtualEndDate) { // picking end
            this._hasVirtualEndDate = false;
            // If date is before the start date then user is picking dates in end-start order
            if (date.isBefore(this.startDate, "day")) {
                const newEndDate = this.startDate.clone();
                this.setStartDate(date.clone());
                this.setEndDate(newEndDate);
            } else {
                this.setEndDate(date.clone());
            }
        } else { // picking start
            this._hasVirtualEndDate = true;
            this.setStartDate(date.clone());
            this.setEndDate(date.clone());
        }
    
        this.updateView();
    },
    
    calculateChosenLabel() {
        // Return earlier if either startDate or endDate are not set
        // https://github.com/Pazien/web-application/issues/4870
        if (!this.startDate || !this.endDate) return;
        
        let customRange = true;
        let i = 0;
        // eslint-disable-next-line guard-for-in,no-restricted-syntax
        for (const range in this.ranges) {
            // ignore times when comparing dates if time picker is not enabled
            if (this.startDate.format("YYYY-MM-DD") === this.ranges[range][0].format("YYYY-MM-DD") && this.endDate.format("YYYY-MM-DD") === this.ranges[range][1].format("YYYY-MM-DD")) {
                customRange = false;
                this.chosenLabel = this.container.find(`.ranges li:eq(${i})`).addClass("active").html();
                break;
            }
            i++;
        }
        if (customRange) {
            this.chosenLabel = null;
            this.showCalendars();
        }
    },
    
    handleApply(e) {
        this.hide();
        this.element.trigger("apply.daterangepicker", this);
    },
    
    handleCancel(e) {
        this.startDate = this.oldStartDate;
        this.endDate = this.oldEndDate;
        this.hide();
        this.element.trigger("cancel.daterangepicker", this);
    },
    
    clickApply(e) {
        this.beforeApply().then(() => {
            this.handleApply(e);
        }).catch(() => {
            this.startDate = this.oldStartDate;
            this.endDate = this.oldEndDate;
            this.updateView();
            this.hide();
        });
    },
    
    clickCancel(e) {
        this.handleCancel(e);
    },
    
    monthOrYearChanged(e) {
        const isLeft = $(e.target).closest(".calendar").hasClass("left");
        const leftOrRight = isLeft ? "left" : "right";
        const cal = this.container.find(`.calendar.${leftOrRight}`);
        
        // Month must be Number for new moment versions
        let month = Number.parseInt(cal.find(".monthselect").val(), 10);
        let year = cal.find(".yearselect").val();
        
        if (!isLeft && this.startDate && (year < this.startDate.year() || (year === this.startDate.year() && month < this.startDate.month()))) {
            month = this.startDate.month();
            year = this.startDate.year();
        }
        
        if (this.minDate && (year < this.minDate.year() || (year === this.minDate.year() && month < this.minDate.month()))) {
            month = this.minDate.month();
            year = this.minDate.year();
        }
        
        if (this.maxDate && (year > this.maxDate.year() || (year === this.maxDate.year() && month > this.maxDate.month()))) {
            month = this.maxDate.month();
            year = this.maxDate.year();
        }
        
        if (isLeft) {
            this.leftCalendar.month.month(month).year(year);
            if (this.linkedCalendars) { this.rightCalendar.month = this.leftCalendar.month.clone().add(1, "month"); }
        } else {
            this.rightCalendar.month.month(month).year(year);
            if (this.linkedCalendars) { this.leftCalendar.month = this.rightCalendar.month.clone().subtract(1, "month"); }
        }
        this.updateCalendars();
    },
    
    formInputsChanged(e) {
        const isRight = $(e.target).closest(".calendar").hasClass("right");
        let start = moment(this.container.find("input[name=\"daterangepicker_start\"]").val(), this.locale.format);
        const end = moment(this.container.find("input[name=\"daterangepicker_end\"]").val(), this.locale.format);
        
        if (start.isValid() && end.isValid()) {
            if (isRight && end.isBefore(start)) { start = end.clone(); }
            
            this.setStartDate(start);
            this.setEndDate(end);
            
            if (isRight) {
                this.container.find("input[name=\"daterangepicker_start\"]").val(this.startDate.format(this.locale.format));
            } else {
                this.container.find("input[name=\"daterangepicker_end\"]").val(this.endDate.format(this.locale.format));
            }
        }
        
        this.updateView();
    },
    
    formInputsFocused(e) {
        // Highlight the focused input
        this.container.find("input[name=\"daterangepicker_start\"], input[name=\"daterangepicker_end\"]").removeClass("active");
        $(e.target).addClass("active");
        
        // Set the state such that if the user goes back to using a mouse,
        // the calendars are aware we're selecting the end of the range, not
        // the start. This allows someone to edit the end of a date range without
        // re-selecting the beginning, by clicking on the end date input then
        // using the calendar.
        const isRight = $(e.target).closest(".calendar").hasClass("right");
        if (isRight) {
            this.endDate = null;
            if (this.startDate) {
                this.setStartDate(this.startDate.clone());
            }
            this.updateView();
        }
    },
    
    formInputsBlurred(e) {
        // this function has one purpose right now: if you tab from the first
        // text input to the second in the UI, the endDate is nulled so that
        // you can click another, but if you tab out without clicking anything
        // or changing the input value, the old endDate should be retained
        
        if (!this.endDate) {
            const val = this.container.find("input[name=\"daterangepicker_end\"]").val();
            const end = moment(val, this.locale.format);
            if (end.isValid()) {
                this.setEndDate(end);
                this.updateView();
            }
        }
    },
    
    elementChanged() {
        if (!this.element.is("input")) return;
        if (!this.element.val().length) return;
        if (this.element.val().length < this.locale.format.length) return;
        
        const dateString = this.element.val().split(this.locale.separator);
        let start = null;
        let end = null;
        
        if (dateString.length === 2) {
            start = moment(dateString[0], this.locale.format);
            end = moment(dateString[1], this.locale.format);
        }
        
        if (start === null || end === null) {
            start = moment(this.element.val(), this.locale.format);
            end = start;
        }
        
        if (!start.isValid() || !end.isValid()) return;
        
        this.setStartDate(start);
        this.setEndDate(end);
        this.updateView();
    },
    
    keydown(e) {
        // hide on tab or enter
        if ((e.keyCode === 9) || (e.keyCode === 13)) {
            this.hide();
        }
    },
    
    updateElement() {
        if (this.element.is("input") && this.autoUpdateInput) {
            this.element.val(this.startDate.format(this.locale.format) + this.locale.separator + this.endDate.format(this.locale.format));
            this.element.trigger("change");
        }
    },
    
    remove() {
        this.container.remove();
        this.element.off(".daterangepicker");
        this.container.off(".daterangepicker");
        this.element.removeData();
    },
    
    handleCompareToChange() {
        if (!this.has_date_comparison) return;
        
        // Find active tab and update its startDate and endDate in compare_to state
        if (this.state.compare_to[this.state.activeCompareToTab]) {
            this.state.compare_to[this.state.activeCompareToTab] = {
                startDate: this.startDate,
                endDate: this.endDate
            };
        }
        
        const tabs = this.container.find(".calendar-tabs");
        
        // Empty and add "base" daterange
        tabs.empty();
        
        // Add up to two date comparison ranges
        if (this.state.compare_to.length > 3) {
            throw new Error("Maximum number of supported comparison date ranges is two");
        }
        
        const makeTab = ({ active, startDate, endDate, showRemoveIcon }) => {
            const formatTabName = () => {
                if (startDate === null && endDate === null) return "Select date range";
                
                let startDateFormatted = "";
                if (startDate) {
                    startDateFormatted = startDate.format("MMM D, YYYY");
                }
                
                let endDateFormatted = "";
                if (endDate) {
                    endDateFormatted = endDate.format("MMM D, YYYY");
                }
                
                return `${startDateFormatted} - ${endDateFormatted}`;
            };
            
            return ReactDOMServer.renderToStaticMarkup(
                <div className={classnames("date-tab", `active-${active}`)}>
                    <FontAwesomeIcon className="calendar-icon" icon={faCalendarAlt}/>
                    <div className="tab-label">
                        {formatTabName({})}
                        {showRemoveIcon && <FontAwesomeIcon className="remove-range-icon" icon={faTimesCircle}/>}
                    </div>
                </div>
            );
        };
        
        this.state.compare_to.forEach((d, i, all) => {
            tabs.append(makeTab({
                active: this.state.activeCompareToTab === i,
                startDate: d.startDate,
                endDate: d.endDate,
                showRemoveIcon: all.length > 1
            }));
        });
        
        if (this.state.compare_to.length < 3) {
            tabs.append(`<div class="date-tab add-comparison">
                            <div class="tab-label">+ Add a comparison</div>
                         </div>`);
        }
    
        this.renderRanges();
    },
    
    handleDatetypeChange() {
        const $datetype_picker = this.container.find(".datetype-picker");
        
        const getTooltip = function(button) {
            try {
                destroyPopovers();
                if (button.tooltip) {
                    return (
                        <i
                            className="fal fa-question-circle pz-popover fraud-tooltip"
                            role="button"
                            data-html="true"
                            data-toggle="popover"
                            data-trigger="hover"
                            data-placement="top"
                            data-container="body"
                            data-content={ReactDOMServer.renderToStaticMarkup(
                                <>
                                    <div><b>{button.name}</b></div>
                                    {button.tooltip}
                                </>
                            )}
                            data-original-title=""
                            title=""
                        />
                    );
                }
            } catch {
                return "";
            }
            
            return "";
        };
        
        if (this.datetype.datetypes.length > 1 && this.has_datetype) {
            $datetype_picker.addClass("pz-radio-buttons-2").removeClass("single-type").show();
            const buttons = this.datetype.datetypes.reduce((memo, button, index, all) => {
                const buttonEl = ReactDOMServer.renderToStaticMarkup(
                    <div data-datetype={button.value} className={classnames(" pz-radio-button", { active: button.selected })}>
                        {button.name}
                        {getTooltip(button)}
                    </div>
                );
                return memo + buttonEl;
            }, "");
            $datetype_picker.html(buttons);
        } else if (this.datetype.datetypes.length === 1 && this.has_datetype) {
            const dtype = this.datetype.datetypes[0];
            $datetype_picker.addClass("single-type").removeClass("pz-radio-buttons-2").show();
    
            $datetype_picker.html(ReactDOMServer.renderToStaticMarkup(
                <div>
                    {dtype.name}
                    {getTooltip(dtype)}
                </div>
            ));
        } else {
            $datetype_picker.hide().empty();
        }
    },
    
    setDatetype({ datetypes = this.datetype.datetypes }) {
        // Set state
        this.datetype = {
            datetypes
        };
        // Handle visuals
        this.handleDatetypeChange();
    },
    
    changeDatetype(event) {
        const selectedValue = event.currentTarget.dataset.datetype;
        this.setDatetype({
            datetypes: this.datetype.datetypes.map((d) => {
                d.selected = d.value === selectedValue;
                return d;
            })
        });
    },
    
    changeViewBy(event) {
        this.activeCalendar = event.currentTarget.dataset.viewby;
        this.hide();
        this.renderContainer();
        this.show();
    },
    
    renderContainer() {
        if (this.container) {
            this.container.off(".daterangepicker");
            this.container.remove();
        }
        
        this.options.template = daterangepickerTemplate({
            has_data_availability: this.has_data_availability,
            has_data_availability_legend: this.has_data_availability_legend,
            has_date_comparison: this.has_date_comparison,
            has_datetype: this.has_datetype,
            activeCalendar: this.activeCalendar,
            calendars: this.calendars
        });
    
        this.container = $(this.options.template).appendTo(this.parentEl);
        
        if (typeof this.options.ranges === "object") {
            this.renderRanges();
        }
    
        this.startDate = this.startDate.startOf(this.activeCalendar);
        this.endDate = this.endDate.endOf(this.activeCalendar);
    
        this.container.addClass("show-calendar");
    
        this.container.addClass(`opens${this.opens}`);
    
        // swap the position of the predefined ranges if opens right
        if (typeof this.options.ranges !== "undefined" && this.opens === "right") {
            this.container.find(".ranges").prependTo(this.container.find(".calendar.left").parent());
        }
    
        // apply CSS classes and labels to buttons
        this.container.find(".applyBtn").addClass(this.buttonClasses);
        if (this.applyClass.length) { this.container.find(".applyBtn").addClass(this.applyClass); }
        this.container.find(".applyBtn").html(this.locale.applyLabel);
    
        // Event listeners
        this.container.on("click.daterangepicker", ".close-dropdown-icon", this.clickCancel.bind(this));
    
        this.container.find(".calendar")
            .on("click.daterangepicker", ".prev", this.clickPrev.bind(this))
            .on("click.daterangepicker", ".next", this.clickNext.bind(this))
            .on("mousedown.daterangepicker", "td.available", this.clickDate.bind(this))
            .on("mousedown.daterangepicker", "td.locked", this.clickLockedDate.bind(this))
            .on("mouseenter.daterangepicker", "td.available", this.hoverDate.bind(this))
            .on("mouseleave.daterangepicker", "td.available", this.updateFormInputs.bind(this))
            .on("change.daterangepicker", "select.yearselect", this.monthOrYearChanged.bind(this))
            .on("change.daterangepicker", "select.monthselect", this.monthOrYearChanged.bind(this))
            .on("click.daterangepicker", ".daterangepicker_input input", this.showCalendars.bind(this))
            .on("focus.daterangepicker", ".daterangepicker_input input", this.formInputsFocused.bind(this))
            .on("blur.daterangepicker", ".daterangepicker_input input", this.formInputsBlurred.bind(this))
            .on("change.daterangepicker", ".daterangepicker_input input", this.formInputsChanged.bind(this));
    
        this.container
            .on("click.daterangepicker", "button.applyBtn", this.clickApply.bind(this))
            .on("click.daterangepicker", "li", this.clickRange.bind(this));
    
        if (this.has_datetype) {
            this.container.on("click.daterangepicker", ".datetype-picker .pz-radio-button:not(.active)", this.changeDatetype.bind(this));
        }
    
        if (this.calendars.length > 1) {
            this.container.on("click.daterangepicker", ".viewby-picker .view-by:not(.active)", this.changeViewBy.bind(this));
        }
    
        if (this.has_data_availability) {
            this.container.on("click.daterangepicker", ".go-to-data-status", (e) => {
                e.preventDefault();
                this.hide(e);
                pazienHistory.navigateWithParams("preferences/data", { trigger: true });
            });
        }
    
        if (this.has_date_comparison) {
            this.container.on("click.daterangepicker", ".remove-range-icon", (e) => {
                e.stopPropagation();
                const dateTab = e.currentTarget.closest(".date-tab");
                const tabIndex = Array.from(dateTab.parentNode.children).indexOf(dateTab);
            
                // Set active tab to the previous one if removing active tab
                if (tabIndex <= this.state.activeCompareToTab) {
                    this.state.activeCompareToTab = Math.max(this.state.activeCompareToTab - 1, 0);
                }
            
                this.state.compare_to = this.state.compare_to.filter((t, i) => i !== tabIndex);
                this.setDatesFromActiveTab();
            
                this.updateView();
            });
        
            this.container.on("click.daterangepicker", ".date-tab", (e) => {
                this._hasVirtualEndDate = false;
                if (e.currentTarget.classList.contains("add-comparison")) {
                    // Handle new comparison tab
                    this.state.compare_to = [...this.state.compare_to, {
                        startDate: null,
                        endDate: null
                    }];
                    this.state.activeCompareToTab = this.state.compare_to.length - 1;
                } else {
                    // Handle tabs switch by setting active tab to index of the target inside its parent
                    this.state.activeCompareToTab = Array.from(e.currentTarget.parentNode.children).indexOf(e.currentTarget);
                }
            
                this.setDatesFromActiveTab();
                this.updateView();
            });
        }
    }
};
